/** Vertical intake templates. Every field stays editable after a template is chosen. */

const text = (label, required = false) => ({ type: 'text', label, required, placeholder: '' });
const email = (label, required = false) => ({ type: 'email', label, required, placeholder: '' });
const phone = (label, required = false) => ({ type: 'phone', label, required, placeholder: '' });
const number = (label, required = false) => ({ type: 'number', label, required, placeholder: '' });
const textarea = (label, required = false) => ({ type: 'textarea', label, required, placeholder: '' });
const date = (label, required = false) => ({ type: 'date', label, required, placeholder: '' });
const radio = (label, options, required = false) => ({ type: 'radio', label, required, placeholder: '', options });
const dropdown = (label, options, required = false) => ({ type: 'dropdown', label, required, placeholder: '', options });

/** Every intake opens the same way: who is asking, and how to reach them. */
const contactBlock = () => [
  text('Full name', true),
  email('Email address', true),
  phone('Phone number'),
];

export const FORM_TEMPLATES = [
  {
    id: 'legal-client',
    name: 'Legal client intake',
    category: 'Legal',
    description: 'Collect initial details for legal services.',
    title: 'Legal client intake',
    fields: [
      ...contactBlock(),
      textarea('Matter type'),
      textarea('Brief description of your matter'),
      textarea('Preferred contact time'),
    ],
  },
  {
    id: 'estate-planning',
    name: 'Estate planning enquiry',
    category: 'Legal',
    description: 'Collect initial details for legal services.',
    title: 'Estate planning enquiry',
    fields: [
      ...contactBlock(),
      textarea('Planning goals'),
      date('Preferred appointment date'),
      textarea('People to include in the meeting'),
    ],
  },
  {
    id: 'business-legal',
    name: 'Business legal enquiry',
    category: 'Legal',
    description: 'Collect initial details for legal services.',
    title: 'Business legal enquiry',
    fields: [
      ...contactBlock(),
      text('Business name'),
      textarea('Type of legal support requested'),
      textarea('Target timeline'),
    ],
  },
  {
    id: 'accounting-client',
    name: 'Accounting client intake',
    category: 'Accounting',
    description: 'Collect initial details for accounting services.',
    title: 'Accounting client intake',
    fields: [
      ...contactBlock(),
      text('Business name'),
      textarea('Services requested'),
      textarea('Financial year end'),
    ],
  },
  {
    id: 'tax-preparation',
    name: 'Tax preparation intake',
    category: 'Accounting',
    description: 'Collect initial details for accounting services.',
    title: 'Tax preparation intake',
    fields: [
      ...contactBlock(),
      textarea('Tax year'),
      radio('Individual or business enquiry', ['Individual', 'Business']),
      textarea('Topics to discuss with your accountant'),
    ],
  },
  {
    id: 'bookkeeping',
    name: 'Bookkeeping intake',
    category: 'Accounting',
    description: 'Collect initial details for accounting services.',
    title: 'Bookkeeping intake',
    fields: [
      ...contactBlock(),
      text('Business name'),
      textarea('Approximate monthly transaction count'),
      textarea('Current bookkeeping software'),
    ],
  },
  {
    id: 'payroll',
    name: 'Payroll service intake',
    category: 'Accounting',
    description: 'Collect initial details for accounting services.',
    title: 'Payroll service intake',
    fields: [
      ...contactBlock(),
      text('Business name'),
      number('Number of employees'),
      dropdown('Pay frequency', ['Weekly', 'Every two weeks', 'Twice a month', 'Monthly', 'Quarterly', 'Annually', 'Other']),
    ],
  },
  {
    id: 'healthcare-enquiry',
    name: 'Healthcare appointment intake',
    category: 'Healthcare',
    description: 'Collect initial details for healthcare services.',
    title: 'Healthcare appointment intake',
    fields: [
      ...contactBlock(),
      textarea('Clinic or service requested'),
      date('Preferred appointment date'),
      textarea('Accessibility or communication needs'),
    ],
  },
  {
    id: 'dental-enquiry',
    name: 'Dental appointment intake',
    category: 'Healthcare',
    description: 'Collect initial details for healthcare services.',
    title: 'Dental appointment intake',
    fields: [
      ...contactBlock(),
      radio('New or returning patient', ['New patient', 'Returning patient']),
      date('Preferred appointment date'),
      textarea('Accessibility or communication needs'),
    ],
  },
  {
    id: 'therapy-enquiry',
    name: 'Therapy appointment enquiry',
    category: 'Healthcare',
    description: 'Collect initial details for healthcare services.',
    title: 'Therapy appointment enquiry',
    fields: [
      ...contactBlock(),
      textarea('Service you would like to discuss'),
      dropdown('Preferred appointment format', ['In person', 'Video call', 'Phone call']),
      textarea('Preferred contact time'),
    ],
  },
  {
    id: 'agency-project',
    name: 'Agency project intake',
    category: 'Agencies',
    description: 'Collect initial details for agency services.',
    title: 'Agency project intake',
    fields: [
      ...contactBlock(),
      text('Company name'),
      textarea('Project goals'),
      textarea('Target launch date'),
    ],
  },
  {
    id: 'design-brief',
    name: 'Design project brief',
    category: 'Agencies',
    description: 'Collect initial details for agency services.',
    title: 'Design project brief',
    fields: [
      ...contactBlock(),
      text('Brand or business name'),
      textarea('Deliverables needed'),
      textarea('Project timeline'),
    ],
  },
  {
    id: 'marketing-brief',
    name: 'Marketing campaign intake',
    category: 'Agencies',
    description: 'Collect initial details for agency services.',
    title: 'Marketing campaign intake',
    fields: [
      ...contactBlock(),
      text('Company name'),
      textarea('Campaign goals'),
      textarea('Target audience'),
    ],
  },
  {
    id: 'web-project',
    name: 'Website project intake',
    category: 'Agencies',
    description: 'Collect initial details for agency services.',
    title: 'Website project intake',
    fields: [
      ...contactBlock(),
      text('Company name'),
      textarea('Website goals'),
      textarea('Features you need'),
    ],
  },
  {
    id: 'consulting-intake',
    name: 'Consulting engagement intake',
    category: 'Professional services',
    description: 'Collect initial details for professional services.',
    title: 'Consulting engagement intake',
    fields: [
      ...contactBlock(),
      text('Organization name'),
      textarea('Challenge you want to address'),
      textarea('Desired outcome'),
    ],
  },
];

export function instantiateTemplate(template) {
  return template.fields.map((item) => ({
    id: crypto.randomUUID(),
    ...structuredClone(item),
  }));
}
